import {FormEvent, useState} from "react";
import {Alert, Button, Form} from "react-bootstrap";
import {Link, useNavigate} from "react-router";
import {SupportApi} from "../../api/SupportApi";
import {ProductsApi} from "../../api/ProductsApi";
import {
    PRESS_PUBLICATION_MAX_LENGTH, PRESS_ROLE_MAX_LENGTH, PRESS_URL_MAX_LENGTH,
    SUPPORT_MESSAGE_MAX_LENGTH, SUPPORT_NAME_MAX_LENGTH, SUPPORT_SUBJECT_MAX_LENGTH
} from "../../api/models";
import {useAuth} from "../../auth/AuthContext";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert, fieldError} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {Field, FieldControl, FieldSelect} from "../../components/Field";
import {SubmitButton} from "../../components/SubmitButton";
import {announce} from "../../components/announce";
import {emailProblem} from "../../components/format";
import {turnstileAvailable} from "../../components/Turnstile";
import {useTurnstile} from "../../components/useTurnstile";
import {format, press, rich, support} from "../../i18n";
import {pressProducts} from "./PressPage";

const messages = press.contact;
const problemsText = messages.problems;

const isWebAddress = (value: string) => {
    try {
        const url = new URL(value.trim());
        return (url.protocol === 'https:' || url.protocol === 'http:') && url.hostname.includes('.');
    } catch {
        return false;
    }
};

// A press inquiry (a support ticket of category 'press'), for journalists, bloggers and YouTubers: who they are and
// where they publish, the product it's about, and if they ask for a review license, what lets us check them (the
// outlet's address and their role). Signed out, as on Contact us, the API emails a private link
// to it; signed in, it opens the ticket. Under /press, not /support, so search engines find it (prerendered).
export function PressContactPage() {
    const navigate = useNavigate();
    const {user, loading: authLoading, logout} = useAuth();
    const {data: catalog} = useAsync(() => ProductsApi.getPublished(), []);
    // The press page's products, or with none there yet, every published one.
    const products = catalog ? (pressProducts(catalog).length ? pressProducts(catalog) : catalog.filter(x => x.published)) : [];
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [publication, setPublication] = useState('');
    const [productId, setProductId] = useState('');
    const [reviewLicense, setReviewLicense] = useState(false);
    const [outletUrl, setOutletUrl] = useState('');
    const [role, setRole] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const turnstile = useTurnstile();
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const [sentTo, setSentTo] = useState<string>();

    // With only one product, a review license is for that one.
    const chosenProduct = productId || (reviewLicense && products.length === 1 ? products[0].id : '');
    const problems = {
        name: !user && !name.trim() ? problemsText.name : undefined,
        email: !user ? (!email.trim() ? problemsText.email : emailProblem(email)) : undefined,
        publication: !publication.trim() ? problemsText.publication : undefined,
        productId: reviewLicense && !chosenProduct ? problemsText.product : undefined,
        outletUrl: reviewLicense && !isWebAddress(outletUrl) ? problemsText.outletUrl : undefined,
        role: reviewLicense && !role.trim() ? problemsText.role : undefined,
        subject: !subject.trim() ? problemsText.subject : undefined,
        body: !body.trim() ? problemsText.body : undefined
    };

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (Object.values(problems).some(Boolean)) {
            return;
        }
        setBusy(true);
        setError(undefined);
        const turnstileToken = await turnstile.waitForToken();
        if (!turnstileToken) {
            setBusy(false);
            return;
        }
        try {
            const opened = await SupportApi.open({
                ...(user ? {} : { name: name.trim(), email: email.trim() }),
                category: 'press',
                publication: publication.trim(),
                ...(chosenProduct ? { productId: chosenProduct } : {}),
                ...(reviewLicense ? { reviewLicense: true, outletUrl: outletUrl.trim(), role: role.trim() } : {}),
                subject: subject.trim(),
                body: body.trim(),
                turnstileToken
            }, []);
            announce(messages.sent);
            if (user) {
                navigate(`/support/${opened.id}`);
            } else {
                setSentTo(email.trim());
            }
        } catch (err) {
            setError(err);
            turnstile.reset();
        } finally {
            setBusy(false);
        }
    };

    const header = <PageHeader title={messages.title} description={messages.description} path="/press/contact"/>;
    if (authLoading) {
        return <>{header}<LoadingSpinner/></>;
    }
    if (!turnstileAvailable()) {
        return <>{header}<Alert variant="secondary">{messages.unavailable}</Alert></>;
    }
    if (sentTo) {
        return <>{header}<Alert variant="success" role="status">{format(messages.checkEmail, { email: sentTo })}</Alert></>;
    }
    const shownError = (field: keyof typeof problems) => (validated && problems[field]) || fieldError(error, field);

    return (
        <>
            {header}
            <p className="text-body-secondary">
                {rich(user ? messages.signedInIntro : messages.intro, { link: text => <Link to="/press">{text}</Link> })}
            </p>
            {user && (
                <p className="text-body-secondary">
                    {rich(messages.signedInEmail, {
                        strong: text => <strong>{text}</strong>,
                        // Signed out, the form asks for a name and address instead.
                        signOut: text => <Button variant="link" className="p-0 align-baseline" disabled={busy} onClick={() => void logout()}>{text}</Button>
                    }, { email: user.emailAddress })}
                </p>
            )}
            <Form noValidate onSubmit={submit} className="support-form">
                <ErrorAlert error={error} shownFields={['name', 'email', 'publication', 'productId', 'outletUrl', 'role', 'subject', 'body']} messages={{
                    429: user ? messages.tooMany : messages.tooManySignedOut,
                    turnstile_failed: support.newTicketPage.turnstileFailed,
                    503: messages.unavailable
                }}/>
                {!user && (
                    <>
                        <Field className="mb-3" controlId="press-name" label={messages.name} error={shownError('name')}>
                            <FieldControl value={name} onChange={e => setName(e.target.value)} maxLength={SUPPORT_NAME_MAX_LENGTH} autoComplete="name" disabled={busy}/>
                        </Field>
                        <Field className="mb-3" controlId="press-email" label={messages.email} help={messages.emailHelp} error={shownError('email')}>
                            <FieldControl type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" disabled={busy}/>
                        </Field>
                    </>
                )}
                <Field className="mb-3" controlId="press-publication" label={messages.publication} help={messages.publicationHelp} error={shownError('publication')}>
                    <FieldControl value={publication} onChange={e => setPublication(e.target.value)} maxLength={PRESS_PUBLICATION_MAX_LENGTH} autoComplete="organization" disabled={busy}/>
                </Field>
                {products.length > 0 && (
                    <Field className="mb-3" controlId="press-product" label={messages.product} error={shownError('productId')}>
                        <FieldSelect value={chosenProduct} onChange={e => setProductId(e.target.value)} disabled={busy}>
                            <option value="">{messages.noProduct}</option>
                            {products.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                        </FieldSelect>
                    </Field>
                )}
                <Form.Group className="mb-3" controlId="press-review-license">
                    <Form.Check type="checkbox" label={messages.reviewLicense} checked={reviewLicense} disabled={busy}
                                aria-describedby={reviewLicense ? 'press-review-license-help' : undefined} onChange={e => setReviewLicense(e.target.checked)}/>
                </Form.Group>
                {reviewLicense && (
                    <fieldset className="border rounded p-3 mb-3">
                        <p id="press-review-license-help" className="small text-body-secondary">{user ? messages.reviewLicenseHelpSignedIn : messages.reviewLicenseHelp}</p>
                        <Field className="mb-3" controlId="press-outlet-url" label={messages.outletUrl} error={shownError('outletUrl')}>
                            <FieldControl type="url" value={outletUrl} placeholder={messages.outletUrlPlaceholder} onChange={e => setOutletUrl(e.target.value)}
                                          maxLength={PRESS_URL_MAX_LENGTH} autoComplete="url" disabled={busy}/>
                        </Field>
                        <Field className="mb-0" controlId="press-role" label={messages.role} error={shownError('role')}>
                            <FieldControl value={role} placeholder={messages.rolePlaceholder} onChange={e => setRole(e.target.value)}
                                          maxLength={PRESS_ROLE_MAX_LENGTH} autoComplete="organization-title" disabled={busy}/>
                        </Field>
                    </fieldset>
                )}
                <Field className="mb-3" controlId="press-subject" label={messages.subject} error={shownError('subject')}>
                    <FieldControl value={subject} onChange={e => setSubject(e.target.value)} maxLength={SUPPORT_SUBJECT_MAX_LENGTH} disabled={busy}/>
                </Field>
                <Field className="mb-4" controlId="press-body" label={messages.body} help={messages.bodyHelp} error={shownError('body')}>
                    <FieldControl as="textarea" rows={8} value={body} onChange={e => setBody(e.target.value)} maxLength={SUPPORT_MESSAGE_MAX_LENGTH} disabled={busy}/>
                </Field>
                <div className="mb-3">
                    {turnstile.widget}
                    {turnstile.failed && <Alert variant="warning">{support.newTicketPage.turnstileUnavailable}</Alert>}
                </div>
                <SubmitButton busy={busy}>{messages.submit}</SubmitButton>
            </Form>
        </>
    );
}
